import { motion } from 'framer-motion';
import { HeartPulse, FlaskConical, Globe, Handshake } from 'lucide-react';

const pillars = [
  { icon: HeartPulse, title: 'Medical & Pharmaceutical', desc: 'Essential applications in diagnostics, antiseptics, and thyroid therapeutics across global healthcare systems.' },
  { icon: FlaskConical, title: 'Chemical & Industrial', desc: 'Critical catalyst and feedstock for specialty chemicals, polymers, and advanced manufacturing processes.' },
  { icon: Globe, title: 'Digital Commodity', desc: 'Tokenized representation enabling transparent, global trading infrastructure for iodine reserves.' },
  { icon: Handshake, title: 'Strategic Partnerships', desc: 'Institutional-grade frameworks for joint ventures, licensing deals, and long-term supply agreements.' },
];

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } }
};

const headerMotion = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-100px' },
  transition: { duration: 0.6 },
};

const gridViewport = { once: true, margin: '-80px' };

function MarketPillarCard({ pillar, index }) {
  return (
    <motion.div
      variants={itemVariants}
      data-testid={`market-pillar-${index}`}
      className="card-glow group rounded-sm border border-[rgba(59,130,246,0.1)] bg-[#060B1E]/70 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[rgba(59,130,246,0.25)]"
    >
      <pillar.icon className="w-6 h-6 text-[#3B82F6] mb-3" strokeWidth={1.5} />
      <div className="text-base font-semibold text-[#F1F5F9]">{pillar.title}</div>
      <p className="mt-2 text-sm leading-relaxed text-[#94A3B8]">{pillar.desc}</p>
    </motion.div>
  );
}

export default function MarketSection() {
  return (
    <section id="market" data-testid="market-section" className="border-y border-[rgba(59,130,246,0.1)]" style={{ background: '#0A1628' }}>
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-2 items-start">
          <motion.div {...headerMotion}>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#3B82F6]">Market Opportunity</div>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-[#F1F5F9]">
              Positioned across multiple high-value sectors.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-[#94A3B8]">
              The commercial narrative centers on iodine's essential role in medical, chemical, and industrial markets, supported by a differentiated resource story and institutional-grade branding.
            </p>
            <div className="mt-8 relative overflow-hidden rounded-sm">
              <img
                src="https://images.unsplash.com/photo-1590859808308-3d2d9c515b1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBkaWdpdGFsJTIwYXNzZXQlMjBuZXR3b3JrfGVufDB8fHx8MTc3NjA3OTQ3OHww&ixlib=rb-4.1.0&q=85"
                alt="Digital asset network"
                className="w-full h-64 object-cover opacity-50"
                data-testid="market-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-transparent to-transparent" />
            </div>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={gridViewport} className="grid gap-4 sm:grid-cols-2">
            {pillars.map((pillar, i) => (
              <MarketPillarCard key={pillar.title} pillar={pillar} index={i} />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
